import { Badge, Ellipsis, Popover } from '@zoom/components';
import classnames from 'classnames';
import { equals } from 'ramda';
import React, { useState, useRef, useEffect, memo } from 'react';

import { Adjustment } from '../config';
import { AdjustmentTileContent } from './AdjustmentTileContent';

export interface AdjustmentsPanelItemProps {
  adjustment: Adjustment;
  disabled: boolean;
  changeHoveredAdjustmentId: (adjustmentId: string | null) => void;
  inCollision: boolean;
  isOpen: boolean;
  previewAndCloseAllOtherAdjustments: (adjustmentId: string) => void;
  setPreOpenedAdjustmentId: (adjustmentId: string[]) => void;
  timezone: string;
}

export const AdjustmentsPanelItemComponent: React.FC<AdjustmentsPanelItemProps> = ({
  adjustment,
  disabled,
  changeHoveredAdjustmentId,
  inCollision,
  isOpen,
  previewAndCloseAllOtherAdjustments,
  setPreOpenedAdjustmentId,
  timezone,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isContainerAttached, setContainerAttached] = useState(false);

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement, MouseEvent> | React.FocusEvent<HTMLElement>) => {
    changeHoveredAdjustmentId(adjustment.id);

    return e;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement, MouseEvent> | React.FocusEvent<HTMLElement>) => {
    changeHoveredAdjustmentId(null);

    return e;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // blur when invoked by Tab key to switch focus
    if (e.key === 'Tab') {
      changeHoveredAdjustmentId(null);
    }
    return e;
  };

  // eslint-disable-next-line consistent-return
  const handleClick = (e: React.MouseEvent) => {
    // blur only when invoked by mouse click
    if (e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) {
      (document.activeElement as HTMLElement).blur();
    }

    if (disabled) {
      return e;
    }

    previewAndCloseAllOtherAdjustments(adjustment.id);
    setPreOpenedAdjustmentId([adjustment.id]);
  };

  return (
    <div data-test-id="adjustments-panel:item" className="adjustment-badge__container" ref={containerRef}>
      <Badge
        className={classnames('adjustment-badge', {
          'adjustment-badge--in-collision': inCollision,
          'adjustment-badge--disabled': disabled,
        })}
        kind="info"
        onClick={handleClick}
        onFocus={handleMouseEnter}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        tag="button"
      >
        {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
        <Ellipsis>{adjustment.name}</Ellipsis>
      </Badge>
      {isContainerAttached && (
        <Popover
          className="adjustment-item__popover-wrapper"
          isOpen={isOpen}
          target={containerRef.current!}
          placement="bottom"
          fade={false}
          boundariesElement="window"
        >
          <AdjustmentTileContent adjustment={adjustment} timezone={timezone} />
        </Popover>
      )}
    </div>
  );
};

export const AdjustmentsPanelItem = memo(
  AdjustmentsPanelItemComponent,
  (previousProps, nextProps) =>
    equals(previousProps.adjustment, nextProps.adjustment) &&
    previousProps.inCollision === nextProps.inCollision &&
    previousProps.isOpen === nextProps.isOpen
);
